import React from 'react';
import {
    calculateTagesstunden,
    calculateWochenstundenFromPlan,
    formatRhythmusForDocument,
    formatTimeForDocument,
    isArbeitszeitEntryComplete,
    sortArbeitsplanByWochentag,
    type ArbeitszeitTagEntry,
    WOCHENTAG_OPTIONS,
} from '../../../utils/pe-papers/arbeitszeitPlan';

const TABLE_CLASS = 'w-full border-collapse my-3 text-[11px] font-sans table-fixed';
const HEAD =
    'border border-slate-300 bg-slate-800 px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wide text-slate-100 align-middle';
const BODY = 'border border-slate-200 px-3 py-2 align-middle text-slate-800';

/** Deutsche Std.-Anzeige mit festen 2 Nachkommastellen (8,00). */
const formatStdForDisplay = (value: number): string => {
    const n = Number.isFinite(value) ? value : 0;
    return n.toFixed(2).replace(/\./g, ',');
};

/** Pause ohne nachgestellten Punkt (kein "60 Minuten." / "0 Min."). */
const formatPauseCell = (pauseMinuten: string): string => {
    const raw = String(pauseMinuten || '')
        .trim()
        .replace(',', '.');
    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) return '0 Min';
    if (num === 1) return '1 Minute';
    return `${Math.round(num)} Minuten`.replace(/\.+$/, '');
};

interface ArbeitszeitPlanDocumentTableProps {
    plan: ArbeitszeitTagEntry[];
    title: string;
    className?: string;
    /** Für Berechnung der Wochenstunden-Summe aus dem Tagesplan */
    wochensatz?: number;
    /** Explizite Wochenstunden (z. B. Formularwert) — hat Vorrang vor Plan-Summe */
    wochenstundenOverride?: number | null;
}

const ArbeitszeitPlanDocumentTable: React.FC<ArbeitszeitPlanDocumentTableProps> = ({
    plan,
    title,
    className = '',
    wochensatz,
    wochenstundenOverride = null,
}) => {
    const rows = sortArbeitsplanByWochentag(plan).filter(isArbeitszeitEntryComplete);
    if (rows.length === 0) return null;

    const fromOverride =
        typeof wochenstundenOverride === 'number' &&
        Number.isFinite(wochenstundenOverride) &&
        wochenstundenOverride > 0
            ? wochenstundenOverride
            : 0;
    const fromPlan =
        typeof wochensatz === 'number' && Number.isFinite(wochensatz) && wochensatz > 0
            ? calculateWochenstundenFromPlan(plan, wochensatz)
            : 0;
    const wochenstundenSumme = fromOverride > 0 ? fromOverride : fromPlan;

    return (
        <div className={`av-table-wrap mb-4 break-inside-avoid page-break-inside-avoid ${className}`}>
            <p className="mb-2 font-sans text-[10px] font-bold uppercase tracking-wider text-slate-600">
                {title}
            </p>
            <table className={TABLE_CLASS}>
                <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '24%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th className={HEAD}>Wochentag</th>
                        <th className={HEAD}>Von</th>
                        <th className={HEAD}>Bis</th>
                        <th className={HEAD}>Pause</th>
                        <th className={HEAD}>Std./Tag</th>
                        <th className={HEAD}>Rhythmus</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((entry) => (
                        <tr key={entry.id}>
                            <td className={BODY}>
                                {WOCHENTAG_OPTIONS.find((d) => d.key === entry.wochentag)?.label || entry.wochentag}
                            </td>
                            <td className={BODY}>{formatTimeForDocument(entry.von)}</td>
                            <td className={BODY}>{formatTimeForDocument(entry.bis)}</td>
                            <td className={BODY}>{formatPauseCell(entry.pauseMinuten)}</td>
                            <td className={BODY}>{formatStdForDisplay(calculateTagesstunden(entry))}</td>
                            <td className={BODY}>{formatRhythmusForDocument(entry)}</td>
                        </tr>
                    ))}
                </tbody>
                {wochenstundenSumme > 0 ? (
                    <tfoot>
                        <tr>
                            <td
                                className={`${BODY} va-tfoot-label font-semibold text-right bg-slate-50`}
                                colSpan={4}
                            >
                                Regelmäßige Wochenstunden
                            </td>
                            <td
                                className={`${BODY} va-tfoot-value font-bold text-right bg-slate-50 whitespace-nowrap`}
                                colSpan={2}
                            >
                                {formatStdForDisplay(wochenstundenSumme)} Std./Woche
                            </td>
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    );
};

export default ArbeitszeitPlanDocumentTable;
